import {envIsDev, envIsTest} from '../../config';
import type {ActionResult} from '../actionResult';
import type {SelectedRun} from '../composer';
import type {ContentPart} from '../store/thread';

/** UnavailableReason explains why the agent chat feature is unavailable. */
export type UnavailableReason =
  | 'feature-flag-disabled'
  | 'not-public-cloud'
  | 'logged-out'
  | 'personal-entity'
  | 'org-setting-disabled'
  | 'loading';

/** AgentChatAvailability reports whether the agent chat feature is enabled. */
export interface AgentChatAvailability {
  /** Whether the feature is enabled (gate + org settings pass). */
  isAvailable: boolean;
  unavailableReason?: UnavailableReason;
}

/** SendMessageOptions controls how `sendMessage` delivers a prompt. */
export interface SendMessageOptions {
  /** Start a fresh thread before sending. */
  newChat?: boolean;
}

/**
 * WBAgentController is the set of agent chat capabilities exposed to the rest
 * of the app, such as opening the chat or sending a message.
 */
export interface WBAgentController {
  /**
   * addRunToDraft attaches a run chip to the active thread's composer,
   * reporting `readonly` when the chat is locked.
   */
  addRunToDraft(run: SelectedRun): ActionResult<'readonly'>;
  openChat(): void;
  openChatToThread(threadId: string): void;
  /**
   * sendMessage delivers a prompt best-effort. It reports `readonly` when the
   * chat is locked and `empty` when there is nothing to send; the caller
   * doesn't need to check access first.
   */
  sendMessage(
    // A string prompt (optionally with leading `attachments`), or a full
    // ordered list of parts — the composite form that lets a prompt carry
    // structured context (a conversation reference, inline mentions) beside
    // its text.
    prompt: string | ContentPart[],
    /** attachments are prepended to the prompt as leading message parts. */
    attachments?: ContentPart[],
    opts?: SendMessageOptions
  ): ActionResult<'readonly' | 'empty'>;
}

/**
 * WBAgentControllerStore holds the agent chat's feature availability and the
 * imperative handle used to open the chat or send a message. One instance is
 * shared app-wide; tests instantiate their own for isolation.
 *
 * NOTE: Methods use arrow functions so they are bound; important when passing
 * to `useSyncExternalStore`.
 */
export class WBAgentControllerStore {
  private availability: AgentChatAvailability = {
    isAvailable: false,
    unavailableReason: 'loading',
  };

  private handle: WBAgentController | null = null;

  private readonly listeners = new Set<() => void>();

  getAvailability = (): AgentChatAvailability => this.availability;

  setAvailability = (next: AgentChatAvailability): void => {
    if (next === this.availability) {
      return;
    }
    this.availability = next;
    this.emit();
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * registerHandle installs the imperative handle and returns a disposer. Only
   * one handle is expected at a time (a single mount point); a second
   * registration in dev signals a duplicate mount.
   */
  registerHandle = (next: WBAgentController): (() => void) => {
    if (this.handle != null && (envIsDev || envIsTest)) {
      console.warn(
        'WBAgentController handle registered twice; expected a single agent chat mount point.'
      );
    }

    this.handle = next;
    return () => {
      if (this.handle === next) {
        this.handle = null;
      }
    };
  };

  getHandle = (): WBAgentController | null => this.handle;

  private emit(): void {
    // NOTE: Snapshot listeners to avoid mutation during iteration.
    for (const listener of Array.from(this.listeners)) {
      listener();
    }
  }
}

/** wbAgentControllerStore is the shared, app-wide controller store instance. */
export const wbAgentControllerStore = new WBAgentControllerStore();
